import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../shared/Modal/Modal';
import Button from '../shared/Button/Button';
import Loading from '../shared/Loading/Loading';
import StatusBadge from '../shared/StatusBadge/StatusBadge';
import { FormActions, ErrorMessage } from '../shared/Form/Form.styles';
import { DetailList, IvaTag, MoneyTag, Notice } from '../../pages/sales/Sales.styles';
import { ItemList, ItemCard, Chip, LinkButton } from './promoters.styles';
import { cutStatus, kindLabel, promoterSaleStatus, saleSourceLabel } from './promoterLabels';
import { formatMoney } from '../../utils/money';
import { formatPercent } from '../../utils/commission';
import { formatCalendarDate, formatTimestamp } from '../../utils/dates';

export const cutWeekLabel = (cut) =>
  `${formatCalendarDate(cut.weekStart)} – ${formatCalendarDate(cut.weekEnd)}`;

const SaleLine = ({ sale, adjustment }) => {
  const state = promoterSaleStatus(sale.status);
  return (
    <ItemCard data-muted={adjustment || sale.status === 'cancelled'}>
      <div className="head">
        <span className="title">{sale.activityTitle}</span>
        <StatusBadge status={state.status}>{state.label}</StatusBadge>
      </div>
      <div className="meta">
        Vendida el {formatCalendarDate(sale.saleDate)} · {sale.quantity} {sale.quantity === 1 ? 'lugar' : 'lugares'}
        {sale.customerName ? ` · ${sale.customerName}` : ''}
      </div>
      <div className="meta">
        <Chip>{saleSourceLabel(sale.source)}{sale.source === 'in_person' && sale.ticketNumber ? ` · ticket #${sale.ticketNumber}` : ''}</Chip>
        {sale.late && <> <Chip>Tardía</Chip></>}
      </div>
      <div className="money">
        <span>Venta: <strong>{formatMoney(sale.listAmount)}</strong> <IvaTag>IVA incluido</IvaTag></span>
        <span>Comisión {formatPercent(sale.commissionPercent)}: <strong>{formatMoney(sale.commissionAmount)}</strong></span>
      </div>
    </ItemCard>
  );
};

// Detalle de un corte semanal: resumen, ventas incluidas y ajustes (ventas
// canceladas después de un corte anterior, que se descuentan en este). Una sola
// vista para promotor, empresa y admin.
//   viewer 'empresa' + onMarkPaid: muestra "Marcar pagado" mientras esté abierto.
const CutDetailModal = ({ cutId, initialCut, viewer, load, onError, onClose, onMarkPaid, refreshKey, locked = false }) => {
  const [cut, setCut] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Los callbacks del padre cambian en cada render: se leen desde una ref para
  // que la carga dependa solo del corte.
  const callbacks = useRef({});
  callbacks.current = { load, onError, onClose };

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await callbacks.current.load(cutId);
      setCut(data.cut);
    } catch (err) {
      if (callbacks.current.onError && callbacks.current.onError(err)) {
        callbacks.current.onClose();
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cutId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail, refreshKey]);

  const shown = cut || initialCut;
  const sales = (cut && cut.sales) || [];
  const adjustments = (cut && cut.adjustments) || [];
  const state = shown ? cutStatus(shown.status) : null;
  const promoter = cut && cut.promoter;

  return (
    <Modal
      title={shown ? `Corte #${shown.id} · ${cutWeekLabel(shown)}` : 'Corte semanal'}
      onClose={locked ? null : onClose}
      maxWidth="680px"
      labelledBy="cut-title"
    >
      {error && (
        <ErrorMessage role="alert">
          {error} <LinkButton type="button" onClick={fetchDetail}>Reintentar</LinkButton>
        </ErrorMessage>
      )}

      {shown && (
        <>
          <p style={{ marginBottom: 12 }}>
            <StatusBadge status={state.status}>{state.label}</StatusBadge>
          </p>
          <DetailList>
            {viewer !== 'promoter' && shown.promoter && (
              <div className="row"><dt>Promotor</dt><dd>{shown.promoter.name} ({kindLabel(shown.promoter.kind)})</dd></div>
            )}
            {viewer !== 'empresa' && shown.empresa && (
              <div className="row"><dt>Empresa</dt><dd>{shown.empresa.businessName}</dd></div>
            )}
            {viewer === 'empresa' && promoter && promoter.email && (
              <div className="row"><dt>Email del promotor</dt><dd>{promoter.email}</dd></div>
            )}
            {viewer === 'empresa' && promoter && promoter.phone && (
              <div className="row"><dt>Teléfono del promotor</dt><dd>{promoter.phone}</dd></div>
            )}
            <div className="row">
              <dt>Ventas ({shown.salesCount})</dt>
              <dd>{formatMoney(shown.salesAmount)} <IvaTag>IVA incluido</IvaTag></dd>
            </div>
            <div className="row"><dt>Comisión de las ventas</dt><dd>{formatMoney(shown.commissionAmount)}</dd></div>
            <div className="row"><dt>Ajustes ({shown.adjustmentsCount})</dt><dd>{formatMoney(shown.adjustmentsAmount)}</dd></div>
            <div className="row strong">
              <dt>{viewer === 'promoter' ? 'Total a recibir' : 'Total a pagar'}</dt>
              <dd><MoneyTag>{formatMoney(shown.totalAmount)}</MoneyTag></dd>
            </div>
            {shown.generatedAt && (
              <div className="row">
                <dt>Generado</dt>
                <dd>{formatTimestamp(shown.generatedAt)}{shown.generatedBy && shown.generatedBy !== 'system' ? ' · por el administrador' : ''}</dd>
              </div>
            )}
            {shown.status === 'paid' && (
              <>
                <div className="row"><dt>Pagado el</dt><dd>{formatCalendarDate(shown.paidDate)}</dd></div>
                {shown.paidBy && <div className="row"><dt>Lo marcó</dt><dd>{shown.paidBy}</dd></div>}
                {shown.paidNote && <div className="row"><dt>Nota</dt><dd>{shown.paidNote}</dd></div>}
              </>
            )}
          </DetailList>

          {Number(shown.totalAmount) < 0 && (
            <Notice $warn style={{ marginTop: 12 }}>
              Este corte sale <strong>negativo</strong>: hay más ventas canceladas (ajustes) que comisiones nuevas. Es un saldo a favor
              de la empresa; no hay nada que pagar.
            </Notice>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.45 }}>
            BokerHub solo registra el corte: no mueve dinero. El pago al promotor lo hace la empresa por su cuenta y después lo marca como pagado.
          </p>
        </>
      )}

      {loading && !cut && <Loading text="Cargando el corte..." />}

      {cut && (
        <>
          <h3 style={{ fontSize: 'var(--text-base)', margin: '18px 0 8px' }}>Ventas del corte ({sales.length})</h3>
          {sales.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Este corte no tiene ventas nuevas.</p>
          ) : (
            <ItemList>{sales.map((sale) => <SaleLine key={sale.id} sale={sale} />)}</ItemList>
          )}

          <h3 style={{ fontSize: 'var(--text-base)', margin: '18px 0 4px' }}>Ajustes ({adjustments.length})</h3>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.45 }}>
            Ventas de cortes anteriores que se cancelaron después: su comisión se descuenta aquí.
          </p>
          {adjustments.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Sin ajustes.</p>
          ) : (
            <ItemList>{adjustments.map((sale) => <SaleLine key={sale.id} sale={sale} adjustment />)}</ItemList>
          )}
        </>
      )}

      <FormActions>
        <Button type="button" variant="ghost" onClick={onClose}>Cerrar</Button>
        {viewer === 'empresa' && onMarkPaid && shown && shown.status === 'open' && (
          <Button type="button" onClick={() => onMarkPaid(cut || shown)} disabled={!cut}>
            Marcar pagado
          </Button>
        )}
      </FormActions>
    </Modal>
  );
};

export default CutDetailModal;
